/**
 * Photos a customer sends in with an order, showing what they have in mind.
 *
 * The bytes go to our own Firebase, not to Shopify. Shopify has no file upload
 * on a line item property without a paid app, and the team needs to see these
 * in the admin beside the rest of the order, which is where the order already
 * lives.
 *
 * THE ORDER DOES NOT EXIST YET when somebody picks a photo, so the flow is: ask
 * the server for a draft and a short-lived ticket, spend the ticket uploading,
 * then leave the draft id on the cart as a property. The webhook importer trades
 * that id for the paths when the order arrives.
 *
 * Everything here is optional. If the endpoint is down, the fetch fails, or the
 * customer skips the step, the order goes through with no photos and nothing
 * says otherwise. A photo upload must never stand between somebody and paying.
 *
 * Classic script, not a module, matching delivery-date.js and
 * delivery-windows.js. Loaded site-wide from layout/theme.liquid and a no-op on
 * every page with no block on it.
 */
(function () {
  "use strict";

  var BASE = "https://us-central1-twelve-thirty-four.cloudfunctions.net";
  var MAX_PHOTOS = 3;
  /** Long edge, matching the iOS picker so the admin sees one size of photo. */
  var MAX_EDGE = 1600;
  var QUALITY = 0.7;

  function setStatus(root, message) {
    var el = root.querySelector("[data-inspiration-status]");
    if (!el) return;
    el.textContent = message || "";
    el.hidden = !message;
  }

  /**
   * Downscale through a canvas, which also strips EXIF for free. That matters:
   * these are photographs taken inside somebody's house and may carry GPS.
   * Falls back to the original file if anything about the canvas path fails,
   * since the server checks the size anyway.
   */
  function downscale(file) {
    return new Promise(function (resolve) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        URL.revokeObjectURL(url);
        try {
          var scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
          var canvas = document.createElement("canvas");
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(function (blob) {
            resolve(blob || file);
          }, "image/jpeg", QUALITY);
        } catch (e) {
          resolve(file);
        }
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  }

  function toBase64(blob) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () {
        // The reader hands back "data:image/jpeg;base64,AAAA". The server wants
        // the payload alone.
        var result = String(reader.result || "");
        var comma = result.indexOf(",");
        resolve(comma === -1 ? result : result.slice(comma + 1));
      };
      reader.onerror = function () { reject(new Error("read failed")); };
      reader.readAsDataURL(blob);
    });
  }

  /**
   * ONE UPLOADER PER PAGE VIEW, not one per DOM element.
   *
   * Horizon replaces the whole product-details column when the customer changes
   * a variant, which is why this script watches for that at all. The replacement
   * is a NEW element, so an uploader kept on the element came back with no draft
   * id and a count of zero: the visible "2 photos" reset to blank and the photos
   * already in Storage were stranded on a draft the order would never name.
   *
   * The draft belongs to the person filling in the form, not to a div.
   */
  var shared = null;

  function Uploader() {
    this.draftId = null;
    this.token = null;
    this.count = 0;
    this.busy = false;
    /** Thumbnails, kept so a re-rendered block can redraw what is already up. */
    this.thumbs = [];
  }

  function uploader() {
    if (!shared) shared = new Uploader();
    return shared;
  }

  /** One draft per page view, minted on the first photo rather than on load, so
   *  a visitor who never adds one costs the endpoint nothing. */
  Uploader.prototype.ensureDraft = function () {
    var self = this;
    if (this.draftId) return Promise.resolve(true);
    return fetch(BASE + "/startInspirationDraft", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    }).then(function (res) {
      if (!res.ok) throw new Error("draft " + res.status);
      return res.json();
    }).then(function (data) {
      self.draftId = data.draftId;
      self.token = data.token;
      return true;
    });
  };

  Uploader.prototype.remaining = function () {
    return MAX_PHOTOS - this.count;
  };

  Uploader.prototype.upload = function (base64) {
    var self = this;
    return fetch(BASE + "/uploadInspirationPhotoWeb", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ draftId: this.draftId, token: this.token, base64: base64 }),
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw new Error(body && body.error ? body.error : "upload failed");
        self.count = body.count;
        return body;
      });
    });
  };

  /** The two properties the order actually carries. Written only once a photo
   *  has really landed, so an order placed after three failures carries none. */
  Uploader.prototype.stamp = function (root) {
    var draft = root.querySelector("[data-inspiration-draft]");
    var countField = root.querySelector("[data-inspiration-count]");
    if (draft) draft.value = this.count > 0 ? this.draftId : "";
    if (countField) {
      countField.value = this.count > 0
        ? this.count + (this.count === 1 ? " photo" : " photos")
        : "";
    }
  };

  function addThumb(root, blob) {
    var strip = root.querySelector("[data-inspiration-strip]");
    if (!strip) return null;
    strip.hidden = false;
    var wrap = document.createElement("div");
    wrap.className = "__thumb";
    wrap.setAttribute("data-pending", "");
    var img = document.createElement("img");
    img.alt = "";
    img.src = URL.createObjectURL(blob);
    wrap.appendChild(img);
    strip.appendChild(wrap);
    return wrap;
  }

  /** Redraw what is already uploaded, and re-stamp the hidden inputs. Called on
   *  every wire, so a block Horizon has just replaced comes back showing the
   *  customer's photos rather than an empty strip. */
  function restore(root) {
    var up = uploader();
    if (!up.count) return;
    for (var i = 0; i < up.thumbs.length; i++) {
      var t = addThumb(root, up.thumbs[i]);
      if (t) t.removeAttribute("data-pending");
    }
    up.stamp(root);
    var adder = root.querySelector(".__add");
    var label = root.querySelector("[data-inspiration-add-label]");
    if (label) label.textContent = "Add another";
    if (adder && up.remaining() <= 0) adder.setAttribute("data-full", "");
  }

  function wire(root) {
    if (root.__inspirationWired) return;

    var input = root.querySelector("[data-inspiration-input]");
    if (!input) return;
    // Flagged only once there is something to wire. Setting it above the input
    // check meant a block whose markup had not finished streaming was marked
    // wired forever and every later pass skipped it.
    root.__inspirationWired = true;
    var adder = root.querySelector(".__add");

    restore(root);

    input.addEventListener("change", function () {
      var up = uploader();
      var files = Array.prototype.slice.call(input.files || []);
      // Clearing the input is what lets somebody pick the same file again after
      // a failure. Do it before any await, or a second pick can be dropped.
      input.value = "";
      if (!files.length || up.busy) return;
      files = files.slice(0, up.remaining());
      if (!files.length) return;

      up.busy = true;
      setStatus(root, "Sending...");

      up.ensureDraft().then(function () {
        // One at a time. Three parallel uploads off a phone on a shop wifi is
        // how you get three timeouts instead of one photo.
        return files.reduce(function (chain, file) {
          return chain.then(function () {
            return downscale(file).then(function (blob) {
              if (!blob) {
                setStatus(root, "That photo could not be read. Try another one.");
                return null;
              }
              var thumb = addThumb(root, blob);
              return toBase64(blob)
                .then(function (b64) { return up.upload(b64); })
                .then(function () {
                  if (thumb) thumb.removeAttribute("data-pending");
                  up.thumbs.push(blob);
                  up.stamp(root);
                })
                .catch(function (err) {
                  if (thumb && thumb.parentNode) thumb.parentNode.removeChild(thumb);
                  setStatus(root, err && err.message
                    ? err.message
                    : "That photo did not upload. Try again, or continue without it.");
                });
            });
          });
        }, Promise.resolve());
      }).catch(function () {
        setStatus(root, "Photos are not available right now. You can still place your order.");
      }).then(function () {
        up.busy = false;
        var statusEl = root.querySelector("[data-inspiration-status]");
        if (up.count > 0 && statusEl && !statusEl.textContent) setStatus(root, "");
        if (adder) {
          if (up.remaining() <= 0) {
            adder.setAttribute("data-full", "");
          } else {
            var label = root.querySelector("[data-inspiration-add-label]");
            if (label && up.count > 0) label.textContent = "Add another";
          }
        }
      });
    });
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll("[data-inspiration-photos]");
    for (var i = 0; i < roots.length; i++) wire(roots[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { init(document); });
  } else {
    init(document);
  }

  // Horizon re-renders the product details column when a variant changes, which
  // replaces the block and its listener. Same reason delivery-date.js watches.
  document.addEventListener("shopify:section:load", function (e) { init(e.target); });

  // Scoped to the main content, not documentElement. This script loads on EVERY
  // page of the site and is a no-op on nearly all of them, so a whole-document
  // subtree observer made every cart-drawer open, carousel tick and lazy section
  // pay for a querySelectorAll that would never match.
  if (window.MutationObserver) {
    var scope = document.getElementById("MainContent");
    if (scope) {
      new MutationObserver(function () { init(scope); })
        .observe(scope, { childList: true, subtree: true });
    }
  }
})();
